import { useMutation, useQuery } from '@apollo/client/react';
import clsx from 'clsx';
import { useState } from 'react';
import { Globe } from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

import { formatRelative } from '@repo/common';
import { DataTestIds, MyDevicesDocument, RevokeAllDevicesDocument, RevokeDeviceDocument } from '@repo/frontend-shared';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import { toast } from '@repo/sds/utils/toast';
import { BrowserChromeIcon } from '@repo/ui/assets/icons/browser-chrome-icon';
import { BrowserEdgeIcon } from '@repo/ui/assets/icons/browser-edge-icon';
import { BrowserSafariIcon } from '@repo/ui/assets/icons/browser-safari-icon';
import { SessionTrashIcon } from '@repo/ui/assets/icons/session-trash-icon';
import { ConfirmationModal } from '@repo/ui/components/elements/ConfirmationModal/ConfirmationModal';
import { Loader } from '@repo/ui/components/elements/Loader/Loader';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';
import { useCurrentUser } from '@repo/ui/contexts/current-user-context';
import { useDeviceType } from '@repo/ui/hooks/use-device-type';
import { betterAuthSignOut } from '@repo/ui/utils/open-auth-url';

import { resolveSessionBrowser, resolveSessionLabel, type SessionBrowserKind } from './security-sessions-display';

import './security-sessions-section.scss';

const SESSIONS = DataTestIds.COMMON.SECURITY.SESSIONS;

type SessionDevice = {
  id: string;
  isCurrent: boolean;
  lastSeen: string;
  location: string;
  name?: string | null;
  userAgent?: string | null;
};

function SessionBrowserIcon({ kind, large }: { kind: SessionBrowserKind; large: boolean }) {
  const icon =
    kind === 'edge' ? (
      <BrowserEdgeIcon />
    ) : kind === 'chrome' ? (
      <BrowserChromeIcon />
    ) : kind === 'safari' ? (
      <BrowserSafariIcon />
    ) : (
      <Globe aria-hidden />
    );

  return <span className={clsx('SecuritySessions__icon', large && 'SecuritySessions__icon--large')}>{icon}</span>;
}

function SessionSignOutControl({
  busy,
  dataTestId,
  isMobile,
  onClick,
  signOutLabel,
}: {
  busy: boolean;
  dataTestId: string;
  isMobile: boolean;
  onClick: () => void;
  signOutLabel: string;
}) {
  return (
    <SDSPrimitiveButton
      ariaLabel={signOutLabel}
      className="SecuritySessions__signOut"
      dataTestId={dataTestId}
      isDisabled={busy}
      onClick={onClick}
    >
      {isMobile ? <SessionTrashIcon /> : <SDSText color="alert" text={signOutLabel} type="body-medium" />}
    </SDSPrimitiveButton>
  );
}

export function SecuritySessionsSection() {
  const { t } = useTranslation('common');
  const { isMobile } = useDeviceType();
  const { completeLogout } = useCurrentUser();
  const { data, error, loading, refetch } = useQuery(MyDevicesDocument);
  const [revokeDevice, { loading: revokingDevice }] = useMutation(RevokeDeviceDocument);
  const [revokeAllDevices, { loading: revokingAll }] = useMutation(RevokeAllDevicesDocument);
  const [pendingDevice, setPendingDevice] = useState<SessionDevice | null>(null);
  const busy = revokingDevice || revokingAll;
  const devices = data?.myDevices ?? [];
  const pendingLabel = pendingDevice
    ? (resolveSessionLabel(pendingDevice.name, pendingDevice.userAgent) ?? t('security.sessions.thisDevice'))
    : '';

  const handleRevoke = async (device: SessionDevice) => {
    const label = resolveSessionLabel(device.name, device.userAgent) ?? t('security.sessions.thisDevice');

    try {
      const result = await revokeDevice({ variables: { input: { deviceId: device.id } } });
      if (!result.data?.revokeDevice) {
        toast.error(t('security.sessions.revokeError'));
        return;
      }
      setPendingDevice(null);
      toast.success(t('security.sessions.signedOutToast', { device: label }));
      if (device.isCurrent) {
        // The server ended the database session; the standard sign-out endpoint
        // also expires the HttpOnly browser cookie that identified it.
        await betterAuthSignOut().catch(() => undefined);
        await completeLogout();
        return;
      }
      await refetch();
    } catch {
      toast.error(t('security.sessions.revokeError'));
    }
  };

  const handleRevokeInactive = async () => {
    try {
      const result = await revokeAllDevices();
      if (!result.data?.revokeAllDevices) {
        toast.error(t('security.sessions.revokeAllEmpty'));
        return;
      }
      await refetch();
    } catch {
      toast.error(t('security.sessions.revokeAllError'));
    }
  };

  if (loading) {
    return (
      <SettingsCard dataTestId={SESSIONS.LOADING} direction="column" variant="outlined">
        <Loader />
        <SDSText color="text-secondary" text={t('security.sessions.loading')} type="body-medium" />
      </SettingsCard>
    );
  }

  if (error) {
    return (
      <SettingsCard dataTestId={SESSIONS.SECTION} direction="column" variant="outlined">
        <SDSText color="alert" dataTestId={SESSIONS.ERROR} text={t('security.sessions.loadError')} type="body-medium" />
      </SettingsCard>
    );
  }

  return (
    <SettingsCard className="SecuritySessions" dataTestId={SESSIONS.SECTION} direction="column" variant="outlined">
      <SDSFlexbox alignItems={isMobile ? 'flex-start' : 'center'} direction="column" fullWidth gap="text-small">
        <SDSFlexbox alignItems={isMobile ? 'flex-start' : 'flex-start'} direction="column" fullWidth gap="no-design-4">
          <SDSFlexbox alignItems="flex-start" fullWidth justifyContent="space-between">
            <SDSFlexbox direction="column" gap="no-design-4">
              <SDSText text={t('security.sessions.title')} type="h5" />
              <SDSText color="text-secondary" text={t('security.sessions.description')} type="body-medium" />
            </SDSFlexbox>
            {isMobile ? null : (
              <SDSPrimitiveButton
                className="SecuritySessions__signOutInactive"
                dataTestId={SESSIONS.SIGN_OUT_INACTIVE}
                isDisabled={busy}
                onClick={() => void handleRevokeInactive()}
              >
                <SDSText color="alert" text={t('security.sessions.signOutInactive')} type="body-small" />
              </SDSPrimitiveButton>
            )}
          </SDSFlexbox>
          {isMobile ? (
            <SDSPrimitiveButton
              className="SecuritySessions__signOutInactive"
              dataTestId={SESSIONS.SIGN_OUT_INACTIVE}
              isDisabled={busy}
              onClick={() => void handleRevokeInactive()}
            >
              <SDSText color="alert" text={t('security.sessions.signOutInactive')} type="body-small" />
            </SDSPrimitiveButton>
          ) : null}
        </SDSFlexbox>
      </SDSFlexbox>

      <div className={clsx('SecuritySessions__table', isMobile && 'SecuritySessions__table--mobile')}>
        <div className="SecuritySessions__header">
          <SDSText text={t('security.sessions.browserDevice')} type="body-small" />
          {isMobile ? null : <SDSText text={t('security.sessions.location')} type="body-small" />}
          {isMobile ? null : <SDSText align="right" text={t('security.sessions.lastAccessed')} type="body-small" />}
          <SDSText align="right" text={t('security.sessions.manage')} type="body-small" />
        </div>
        {devices.map(device => {
          const label = resolveSessionLabel(device.name, device.userAgent) ?? t('security.sessions.thisDevice');
          const kind = resolveSessionBrowser(`${device.name ?? ''} ${device.userAgent ?? ''} ${label}`);
          const lastAccessed = device.isCurrent
            ? t('security.sessions.currentSession')
            : formatRelative(device.lastSeen);

          return (
            <div className="SecuritySessions__row" data-testid={SESSIONS.ROW} key={device.id}>
              <SDSFlexbox alignItems="center" gap="text-small">
                <SessionBrowserIcon kind={kind} large={isMobile} />
                <SDSFlexbox direction="column" gap="no-design-4">
                  <SDSText text={label} type="body-medium" />
                  {isMobile ? (
                    <>
                      <SDSText color="text-secondary" text={device.location} type="body-small" />
                      <SDSText
                        color={device.isCurrent ? 'primary' : 'text-secondary'}
                        text={lastAccessed}
                        type="body-small"
                      />
                    </>
                  ) : null}
                </SDSFlexbox>
              </SDSFlexbox>
              {isMobile ? null : <SDSText color="text-secondary" text={device.location} type="body-medium" />}
              {isMobile ? null : (
                <SDSText
                  align="right"
                  color={device.isCurrent ? 'primary' : 'text-secondary'}
                  text={lastAccessed}
                  type="body-medium"
                />
              )}
              <SessionSignOutControl
                busy={busy}
                dataTestId={device.isCurrent ? SESSIONS.SIGN_OUT_CURRENT : SESSIONS.SIGN_OUT}
                isMobile={isMobile}
                onClick={() => setPendingDevice(device)}
                signOutLabel={t('security.sessions.signOut')}
              />
            </div>
          );
        })}
      </div>
      <ConfirmationModal
        buttonText={t('security.sessions.deleteConfirm')}
        isConfirming={revokingDevice}
        isOpen={pendingDevice !== null}
        onClose={() => setPendingDevice(null)}
        onConfirm={() => {
          if (!pendingDevice) {
            return;
          }
          void handleRevoke(pendingDevice);
        }}
        size="small"
        title={t('security.sessions.deleteConfirmTitle', { device: pendingLabel })}
      />
    </SettingsCard>
  );
}
